import { useEffect, useMemo, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderCircleIcon, TriangleAlertIcon } from 'lucide-react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useAppDispatch } from '@/app/hooks';
import { addToast } from '@/app/slices/toast';
import {
  StringMapAttributeName,
  StringMapObjectTypeCode,
} from '@/components/common/string-map-dropdown';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useAccessToken } from '@/services/auth';
import {
  soScheduleEditOptions,
  type SoScheduleEditResponse,
  useUpdateSoScheduleRestMutation,
} from '@/services/donor-scheduling';
import { useGetStringMapsQuery } from '@/services/lookups/hooks';
import { parseWallClock, toWallClockISO } from '@/utils';

import {
  CLINIC_RANCH_HAUL_IN_OPTIONS,
  TARGET_TIME_OPTIONS,
} from '../../constants';
import { TRUE_FALSE_SELECT_OPTIONS } from '../donor-schedule-form/constants';
import {
  type EditSoScheduleFormData,
  editSoScheduleSchema,
  isSoScheduleLocked,
  SO_SCHEDULE_TEXT_MAX_LENGTH,
} from './schema';
import {
  SoScheduleLookupCombobox,
  type SoScheduleLookupKind,
} from './SoScheduleLookupCombobox';

const DIALOG_Z_INDEX = 'z-[5000]';

type EditSoScheduleDialogProps = {
  visible: boolean;
  soScheduleId: string | null | undefined;
  onHide: () => void;
  /** Called after the dialog closes, with the saved SO schedule and its values before the edit. */
  onSaved?: (
    soSchedule: SoScheduleEditResponse,
    previous: SoScheduleEditResponse,
  ) => void;
};

const EMPTY_FORM: EditSoScheduleFormData = {
  donor: null,
  scheduleMaster: null,
  description: '',
  targetDate: null,
  targetTime: null,
  site: null,
  fresh: null,
  fshDrug1: null,
  fshDrug2: null,
  comment: '',
  clinicRanchHaulIn: null,
  billDrugs: null,
  drugShipFee: null,
  fshNext: null,
  owner: null,
  billStatus: null,
};

const toLookupRef = (
  ref: SoScheduleEditResponse['donor'],
): EditSoScheduleFormData['donor'] =>
  ref?.id ? { id: ref.id, name: ref.name ?? null } : null;

const toFormData = (
  soSchedule: SoScheduleEditResponse,
): EditSoScheduleFormData => ({
  donor: toLookupRef(soSchedule.donor),
  scheduleMaster: toLookupRef(soSchedule.scheduleMaster),
  description: soSchedule.description ?? '',
  targetDate: parseWallClock(soSchedule.targetDate),
  targetTime: soSchedule.targetTime ?? null,
  site: toLookupRef(soSchedule.site),
  fresh: soSchedule.fresh ?? null,
  fshDrug1: toLookupRef(soSchedule.fshDrug1),
  fshDrug2: toLookupRef(soSchedule.fshDrug2),
  comment: soSchedule.comment ?? '',
  clinicRanchHaulIn: soSchedule.clinicRanchHaulIn ?? null,
  billDrugs: soSchedule.billDrugs ?? null,
  drugShipFee: soSchedule.drugShipFee ?? null,
  fshNext: soSchedule.fshNext ?? null,
  owner: toLookupRef(soSchedule.owner),
  billStatus: soSchedule.billStatus ?? null,
});

/** Segmented single-choice buttons (Base UI replacement for PrimeReact SelectButton). */
function OptionToggle<T extends boolean | number>({
  id,
  options,
  value,
  onChange,
  disabled,
}: {
  id: string;
  options: { name: string; value: T }[];
  value: T | null;
  onChange: (value: T | null) => void;
  disabled?: boolean;
}) {
  return (
    <div id={id} role="group" className="flex flex-wrap gap-1">
      {options.map((option) => {
        const selected = value === option.value;
        return (
          <Button
            key={String(option.value)}
            type="button"
            size="sm"
            variant={selected ? 'default' : 'outline'}
            aria-pressed={selected}
            disabled={disabled}
            // Clicking the selected option clears it, like the PrimeReact SelectButton.
            onClick={() => onChange(selected ? null : option.value)}
          >
            {option.name}
          </Button>
        );
      })}
    </div>
  );
}

/** Option set (StringMap) dropdown for an SO schedule attribute. */
function StringMapSelect({
  id,
  attributeName,
  value,
  onChange,
  container,
  disabled,
}: {
  id: string;
  attributeName: string;
  value: number | null;
  onChange: (value: number | null) => void;
  container?: HTMLElement | null;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { data = [], isLoading } = useGetStringMapsQuery({
    objectTypeCode: StringMapObjectTypeCode.SO_SCHEDULE,
    attributeName,
  });
  const items = useMemo(
    () =>
      data.map((item) => ({
        value: Number(item.attributeValue),
        label: item.value,
      })),
    [data],
  );

  return (
    <Select
      items={items}
      value={value}
      onChange={(next) => onChange((next as number | null) ?? null)}
      disabled={disabled || isLoading}
    >
      <SelectTrigger id={id} className="w-full">
        <SelectValue placeholder={t('common.selectOption', 'Select Option')} />
      </SelectTrigger>
      <SelectContent container={container}>
        {/* Lets the user clear an optional value. */}
        <SelectItem value={null}>
          {t('common.selectOption', 'Select Option')}
        </SelectItem>
        {items.map((item) => (
          <SelectItem key={item.value} value={item.value}>
            {item.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Edits an SO schedule (ADO 36879). Opened from the grid's Actions menu and from the
 * SO Schedule field on the Schedules tab. Read-only once the donor schedule's day has passed.
 */
export function EditSoScheduleDialog({
  visible,
  soScheduleId,
  onHide,
  onSaved,
}: EditSoScheduleDialogProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const token = useAccessToken();
  const queryClient = useQueryClient();
  // Popups (lookups, selects, calendar) portal here so they stack above the raised dialog.
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  const soScheduleQuery = soScheduleEditOptions(token, soScheduleId);
  const {
    data: soSchedule,
    isLoading,
    isError,
  } = useQuery({
    ...soScheduleQuery,
    enabled: visible && soScheduleQuery.enabled !== false,
  });
  const updateSoSchedule = useUpdateSoScheduleRestMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditSoScheduleFormData>({
    resolver: zodResolver(editSoScheduleSchema),
    defaultValues: EMPTY_FORM,
  });

  useEffect(() => {
    reset(soSchedule ? toFormData(soSchedule) : EMPTY_FORM);
  }, [soSchedule, reset]);

  const isLocked = useMemo(
    () => isSoScheduleLocked(soSchedule?.donorScheduleDate),
    [soSchedule?.donorScheduleDate],
  );
  const disabled = isLocked || !soSchedule || updateSoSchedule.isPending;

  const onSubmit = async (formData: EditSoScheduleFormData) => {
    if (!soScheduleId || !soSchedule || !formData.targetDate) return;
    const previous = soSchedule;

    try {
      const saved = await updateSoSchedule.mutateAsync({
        soScheduleId,
        request: {
          donorId: formData.donor!.id,
          scheduleMasterId: formData.scheduleMaster!.id,
          description: formData.description,
          targetDate: toWallClockISO(formData.targetDate),
          targetTime: formData.targetTime,
          siteId: formData.site?.id ?? null,
          fresh: formData.fresh,
          fshDrug1Id: formData.fshDrug1?.id ?? null,
          fshDrug2Id: formData.fshDrug2?.id ?? null,
          comment: formData.comment || null,
          clinicRanchHaulIn: formData.clinicRanchHaulIn,
          billDrugs: formData.billDrugs,
          drugShipFee: formData.drugShipFee,
          fshNext: formData.fshNext,
          ownerId: formData.owner?.id ?? null,
          billStatus: formData.billStatus,
          ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });

      queryClient.setQueryData(soScheduleQuery.queryKey, saved);
      dispatch(
        addToast({
          severity: 'success',
          summary: t('donorScheduling.editSoSchedule.saved'),
          life: ToastLife.DEFAULT,
        }),
      );
      onHide();
      onSaved?.(saved, previous);
    } catch (error) {
      dispatch(
        addToast({
          severity: 'error',
          summary: t('donorScheduling.editSoSchedule.saveFailed'),
          detail: error instanceof Error ? error.message : String(error),
          life: ToastLife.DEFAULT,
        }),
      );
    }
  };

  const label = (key: string) => t(`donorScheduling.editSoSchedule.${key}`);

  const requiredLabel = (htmlFor: string, key: string) => (
    <FieldLabel htmlFor={htmlFor}>
      {label(key)}
      <span className="text-destructive">*</span>
    </FieldLabel>
  );

  const errorText = (message?: string) =>
    message ? <FieldError>{t(message)}</FieldError> : null;

  const lookup = (
    name: keyof Pick<
      EditSoScheduleFormData,
      'donor' | 'scheduleMaster' | 'site' | 'fshDrug1' | 'fshDrug2' | 'owner'
    >,
    kind: SoScheduleLookupKind,
    labelKey: string,
    required = false,
  ) => {
    const id = `so-schedule-${name}`;
    return (
      <Field data-invalid={!!errors[name]}>
        {required ? (
          requiredLabel(id, labelKey)
        ) : (
          <FieldLabel htmlFor={id}>{label(labelKey)}</FieldLabel>
        )}
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <SoScheduleLookupCombobox
              id={id}
              kind={kind}
              value={field.value}
              onChange={field.onChange}
              container={container}
              invalid={!!errors[name]}
              disabled={disabled}
            />
          )}
        />
        {errorText(errors[name]?.message)}
      </Field>
    );
  };

  const stringMap = (
    name: keyof Pick<
      EditSoScheduleFormData,
      'fresh' | 'drugShipFee' | 'fshNext' | 'billStatus'
    >,
    attributeName: string,
    labelKey: string,
  ) => {
    const id = `so-schedule-${name}`;
    return (
      <Field>
        <FieldLabel htmlFor={id}>{label(labelKey)}</FieldLabel>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <StringMapSelect
              id={id}
              attributeName={attributeName}
              value={field.value}
              onChange={field.onChange}
              container={container}
              disabled={disabled}
            />
          )}
        />
      </Field>
    );
  };

  return (
    <Dialog open={visible} onOpenChange={(open) => !open && onHide()}>
      <DialogContent
        ref={setContainer}
        className={cn(DIALOG_Z_INDEX, 'md:max-w-3xl')}
        overlayClassName={DIALOG_Z_INDEX}
      >
        <DialogHeader>
          <DialogTitle>
            {t(
              isLocked
                ? 'donorScheduling.editSoSchedule.viewTitle'
                : 'donorScheduling.editSoSchedule.title',
            )}
          </DialogTitle>
          {soSchedule && !isLocked && (
            <DialogDescription>
              {t('donorScheduling.editSoSchedule.syncNote')}
            </DialogDescription>
          )}
        </DialogHeader>

        <DialogBody>
          {isLoading ? (
            <div className="flex justify-center p-6">
              <LoaderCircleIcon
                className="text-muted-foreground size-6 animate-spin"
                aria-label={t('common.loading', 'Loading')}
              />
            </div>
          ) : isError || !soSchedule ? (
            <p className="text-destructive" role="alert">
              {t('donorScheduling.editSoSchedule.loadFailed')}
            </p>
          ) : (
            <form
              id="edit-so-schedule-form"
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col gap-4"
              data-testid="edit-so-schedule-form"
            >
              {isLocked && (
                <div
                  role="status"
                  className="flex items-center gap-2 rounded-lg bg-amber-500/15 p-3 text-amber-700 dark:text-amber-400"
                >
                  <TriangleAlertIcon className="size-4 shrink-0" />
                  {t('donorScheduling.editSoSchedule.lockedMessage')}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {lookup('donor', 'donor', 'donor', true)}
                {lookup(
                  'scheduleMaster',
                  'scheduleMaster',
                  'scheduleMaster',
                  true,
                )}

                <Field
                  className="md:col-span-2"
                  data-invalid={!!errors.description}
                >
                  {requiredLabel('so-schedule-description', 'description')}
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <Input
                        {...field}
                        id="so-schedule-description"
                        maxLength={SO_SCHEDULE_TEXT_MAX_LENGTH}
                        aria-invalid={!!errors.description}
                        disabled={disabled}
                      />
                    )}
                  />
                  {errorText(errors.description?.message)}
                </Field>

                <Field data-invalid={!!errors.targetDate}>
                  {requiredLabel('so-schedule-target-date', 'targetDate')}
                  <Controller
                    name="targetDate"
                    control={control}
                    render={({ field }) => (
                      <DatePicker
                        id="so-schedule-target-date"
                        value={field.value ?? undefined}
                        onChange={(date) => field.onChange(date ?? null)}
                        container={container}
                        aria-invalid={!!errors.targetDate}
                        disabled={disabled}
                        className="w-full"
                      />
                    )}
                  />
                  {errorText(errors.targetDate?.message)}
                </Field>

                <Field>
                  <FieldLabel htmlFor="so-schedule-target-time">
                    {label('targetTime')}
                  </FieldLabel>
                  <Controller
                    name="targetTime"
                    control={control}
                    render={({ field }) => (
                      <OptionToggle
                        id="so-schedule-target-time"
                        options={TARGET_TIME_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={disabled}
                      />
                    )}
                  />
                </Field>

                {lookup('site', 'site', 'site')}
                {stringMap(
                  'fresh',
                  StringMapAttributeName.SO_SCHEDULE_FRESH,
                  'bothFreshFrozen',
                )}
                {lookup('fshDrug1', 'drug', 'fshDrug1')}
                {lookup('fshDrug2', 'drug', 'fshDrug2')}

                <Field
                  className="md:col-span-2"
                  data-invalid={!!errors.comment}
                >
                  <FieldLabel htmlFor="so-schedule-comment">
                    {label('scheduleComment')}
                  </FieldLabel>
                  <Controller
                    name="comment"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        {...field}
                        id="so-schedule-comment"
                        rows={2}
                        maxLength={SO_SCHEDULE_TEXT_MAX_LENGTH}
                        aria-invalid={!!errors.comment}
                        disabled={disabled}
                      />
                    )}
                  />
                  {errorText(errors.comment?.message)}
                </Field>

                <Field>
                  <FieldLabel htmlFor="so-schedule-clinic-ranch-haul-in">
                    {label('clinicRanchHaulIn')}
                  </FieldLabel>
                  <Controller
                    name="clinicRanchHaulIn"
                    control={control}
                    render={({ field }) => (
                      <OptionToggle
                        id="so-schedule-clinic-ranch-haul-in"
                        options={CLINIC_RANCH_HAUL_IN_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={disabled}
                      />
                    )}
                  />
                </Field>

                <Field>
                  <FieldLabel htmlFor="so-schedule-bill-drugs">
                    {label('billDrugs')}
                  </FieldLabel>
                  <Controller
                    name="billDrugs"
                    control={control}
                    render={({ field }) => (
                      <OptionToggle
                        id="so-schedule-bill-drugs"
                        options={TRUE_FALSE_SELECT_OPTIONS}
                        value={field.value}
                        onChange={field.onChange}
                        disabled={disabled}
                      />
                    )}
                  />
                </Field>

                {stringMap(
                  'drugShipFee',
                  StringMapAttributeName.DRUG_SHIPPING_FEE,
                  'drugShippingHandling',
                )}
                {stringMap(
                  'fshNext',
                  StringMapAttributeName.SO_SCHEDULE_FSH_NEXT,
                  'nextDrug',
                )}
                {lookup('owner', 'owner', 'owner')}
                {stringMap(
                  'billStatus',
                  StringMapAttributeName.SO_SCHEDULE_BILL_STATUS,
                  'billStatus',
                )}
              </div>
            </form>
          )}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onHide}
            disabled={updateSoSchedule.isPending}
          >
            {t('common.cancel')}
          </Button>
          {!isLocked && (
            <Button
              type="submit"
              form="edit-so-schedule-form"
              disabled={disabled}
            >
              {updateSoSchedule.isPending && (
                <LoaderCircleIcon className="animate-spin" />
              )}
              {t('common.save')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
